import React from 'react';
import { Clock, Award, Sparkles, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { DialogDescription, DialogTitle } from 'components/ui/dialog';
import { StatusBadge } from 'components/ui/status-badge';
import { ExamDialog } from './ExamDialog';
import { estimateCategoryScore } from 'modules/test/category-scoring';
import type { ExamScaledReport } from '@examing/types';

export interface ResultDialogProps {
    open: boolean;
    /** Escape / scrim: the runner leaves the finished attempt, as before. */
    onClose: () => void;
    /** Author preview run: nothing was scored. */
    preview: boolean;
    /** From the submit response; absent for a preview. */
    score?: number;
    maxScore?: number;
    /** Some answers still wait for a grader (attempt status SUBMITTED). */
    needsManualGrading: boolean;
    /** Marketplace tests never show a pass mark. */
    isMarketplace: boolean;
    /** The test's pass mark in %, when it has one. */
    passingScore?: number | null;
    onViewDetails: () => void;
    onBackToTests: () => void;
    /** Preview only: back to the editor. */
    onBackToAssessment: () => void;
    /** Optional category and metadata for category-specific estimation (Milliy Sertifikat, DTM, etc.). */
    category?: string | null;
    testTitle?: string | null;
    tags?: string[] | null;
    subjectConfigsCount?: number;
    scaledReport?: ExamScaledReport | null;
    userGoalLevel?: string | null;
}

const EYEBROW = 'text-[13px] font-medium leading-tight text-muted-foreground';
const HEADING = 'mt-1.5 text-[22px] font-bold leading-tight tracking-[-0.02em]';
const ACTIONS = 'mt-6 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end';

const THEME_STYLES: Record<string, { bg: string; border: string; badge: string; text: string }> = {
    gold: {
        bg: 'bg-amber-500/10 dark:bg-amber-500/15',
        border: 'border-amber-400/30 dark:border-amber-400/40',
        badge: 'bg-gradient-to-r from-amber-500 to-yellow-500 text-neutral-950 shadow-amber-500/20',
        text: 'text-amber-950 dark:text-amber-100',
    },
    emerald: {
        bg: 'bg-emerald-500/10 dark:bg-emerald-500/15',
        border: 'border-emerald-400/30 dark:border-emerald-400/40',
        badge: 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-emerald-500/20',
        text: 'text-emerald-950 dark:text-emerald-100',
    },
    blue: {
        bg: 'bg-blue-500/10 dark:bg-blue-500/15',
        border: 'border-blue-400/30 dark:border-blue-400/40',
        badge: 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-blue-500/20',
        text: 'text-blue-950 dark:text-blue-100',
    },
    purple: {
        bg: 'bg-purple-500/10 dark:bg-purple-500/15',
        border: 'border-purple-400/30 dark:border-purple-400/40',
        badge: 'bg-gradient-to-r from-purple-600 to-indigo-500 text-white shadow-purple-500/20',
        text: 'text-purple-950 dark:text-purple-100',
    },
    amber: {
        bg: 'bg-amber-500/10 dark:bg-amber-500/15',
        border: 'border-amber-400/30 dark:border-amber-400/40',
        badge: 'bg-gradient-to-r from-amber-600 to-orange-500 text-white shadow-amber-500/20',
        text: 'text-amber-950 dark:text-amber-100',
    },
    rose: {
        bg: 'bg-rose-500/10 dark:bg-rose-500/15',
        border: 'border-rose-400/30 dark:border-rose-400/40',
        badge: 'bg-gradient-to-r from-rose-600 to-red-500 text-white shadow-rose-500/20',
        text: 'text-rose-950 dark:text-rose-100',
    },
};

/**
 * "Test yakunlandi": joyful category-aware result dialog.
 * Renders estimated grade (Milliy Sertifikat A+, DTM Single/Mock, IELTS/SAT, Attestatsiya),
 * target goal comparison, celebratory styling, and clear action paths.
 */
export function ResultDialog({
    open,
    onClose,
    preview,
    score,
    maxScore,
    needsManualGrading,
    isMarketplace,
    passingScore,
    onViewDetails,
    onBackToTests,
    onBackToAssessment,
    category,
    testTitle,
    tags,
    subjectConfigsCount,
    scaledReport,
    userGoalLevel,
}: ResultDialogProps) {
    const { t } = useTranslation();

    if (preview) {
        return (
            <ExamDialog open={open} onClose={onClose} widthClass="max-w-[440px]">
                <p className={EYEBROW}>{t('preview_complete')}</p>
                <DialogTitle className={HEADING}>{t('preview_mode')}</DialogTitle>
                <DialogDescription className="mt-2 text-[14.5px] leading-normal">
                    {t('no_results_were_saved_this_is_how_candidates_will_see_the_assessment')}
                </DialogDescription>
                <div className={ACTIONS}>
                    <Button type="button" size="lg" onClick={onBackToAssessment}>
                        {t('back_to_assessment')}
                    </Button>
                </div>
            </ExamDialog>
        );
    }

    const total = maxScore ?? 0;
    const points = score ?? 0;
    const percent = total > 0 ? Math.round((points / total) * 100) : 0;
    const hasPassRate = !isMarketplace && Boolean(passingScore);
    const isPassed = hasPassRate ? percent >= (passingScore || 70) : true;
    const heading = isMarketplace
        ? t('test_completed_title', 'Test yakunlandi!')
        : isPassed
          ? t('congratulations')
          : t('keep_practicing');

    // Calculate category-specific grade / tier (Milliy Sertifikat, DTM, Attestatsiya, IELTS, SAT)
    const categoryResult = estimateCategoryScore({
        percentage: percent,
        score: points,
        maxScore: total,
        passed: isPassed,
        category,
        title: testTitle,
        tags,
        subjectConfigsCount,
        scaledReport,
        userGoalLevel,
    });

    const isSpecialCategory = categoryResult.categoryType !== 'GENERAL';
    const theme = THEME_STYLES[categoryResult.colorTheme] || THEME_STYLES.gold;

    return (
        <ExamDialog open={open} onClose={onClose} widthClass="max-w-[480px]" withDescription={false}>
            <div className="relative">
                <p className={EYEBROW}>{t('assessment_complete')}</p>
                <DialogTitle className={HEADING}>{heading}</DialogTitle>

                {/* Category Hero Badge & Joyful Feedback */}
                {isSpecialCategory && (
                    <div
                        className={`mt-4 rounded-xl border p-4 shadow-sm transition-all ${theme.bg} ${theme.border}`}
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                                    {categoryResult.categoryType === 'MILLIY_SERTIFIKAT'
                                        ? 'Milliy Sertifikat Bahosi'
                                        : categoryResult.categoryType === 'ATTESTATSIYA'
                                          ? 'Attestatsiya Toifasi'
                                          : categoryResult.categoryType === 'DTM_MOCK'
                                            ? 'DTM Umumiy Blok Balli'
                                            : categoryResult.categoryType === 'DTM_SINGLE'
                                              ? 'DTM Fan Tayyorgarligi'
                                              : 'Imtihon Natijasi'}
                                </span>
                                <div className="mt-0.5 text-lg font-extrabold tracking-tight">
                                    {categoryResult.title}
                                </div>
                                {categoryResult.subtitle && (
                                    <p className="mt-0.5 text-[12.5px] font-medium opacity-90">
                                        {categoryResult.subtitle}
                                    </p>
                                )}
                            </div>

                            {categoryResult.scoreDisplay && (
                                <div
                                    className={`flex h-14 min-w-[3.5rem] px-3 items-center justify-center rounded-2xl text-2xl font-black tracking-tight shadow-md ${theme.badge}`}
                                >
                                    {categoryResult.scoreDisplay}
                                </div>
                            )}
                        </div>

                        {/* Goal Comparison Callout */}
                        {categoryResult.goalComparison && (
                            <div className="mt-3 flex items-start gap-2 rounded-lg bg-background/80 p-2.5 text-[12.5px] font-medium leading-relaxed shadow-xs backdrop-blur-xs">
                                {categoryResult.goalComparison.isExceeded ? (
                                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                                ) : categoryResult.goalComparison.isMet ? (
                                    <Award className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                                ) : (
                                    <Target className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                                )}
                                <span>{categoryResult.goalComparison.message}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Standard score and pass metrics */}
                <div className="mt-4 flex items-end justify-between gap-4 border-y border-border py-[18px]">
                    <div className="flex min-w-0 flex-col gap-1.5">
                        <span className="text-[44px] font-bold leading-none tracking-[-0.03em] tabular-nums">
                            {percent}%
                        </span>
                        <span className="text-sm text-muted-foreground">
                            {t('you_scored')}:{' '}
                            <span className="font-semibold tabular-nums text-foreground">
                                {t('exam_score_of_total', { score: points, total })}
                            </span>
                        </span>
                    </div>
                    {hasPassRate && (
                        <div className="flex shrink-0 flex-col items-end gap-2">
                            <StatusBadge tone={isPassed ? 'success' : 'error'} size="md">
                                {isPassed ? t('passed') : t('not_passed')}
                            </StatusBadge>
                            <span className="text-[13px] text-muted-foreground">
                                {t('pass_mark')} <span className="tabular-nums">{passingScore}%</span>
                            </span>
                        </div>
                    )}
                </div>

                {needsManualGrading && (
                    <p className="mt-4 flex gap-2 text-[13.5px] leading-normal text-foreground/80">
                        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-warning-500" aria-hidden />
                        {t('exam_manual_grading_note')}
                    </p>
                )}

                <div className={ACTIONS}>
                    <Button type="button" variant="outline" size="lg" onClick={onViewDetails}>
                        {t('analyze_result')}
                    </Button>
                    <Button type="button" size="lg" onClick={onBackToTests}>
                        {t('back_to_tests')}
                    </Button>
                </div>
            </div>
        </ExamDialog>
    );
}

export default ResultDialog;
